import { PublicClientApplication, EventType } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { ErrorBoundary } from "./ErrorBoundary";
import { missingConfig, msalConfig } from "./auth/config";
import { restoreReturnPath } from "./router";
import "./index.css";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found");
}

// Say what is wrong rather than rendering nothing. A blank page sends you to
// the browser console; this sends you to the file you need to edit.
if (missingConfig.length > 0) {
  container.innerHTML =
    '<main style="font-family:system-ui;max-width:640px;margin:48px auto;padding:0 24px">' +
    "<h1>Canopy cannot start</h1>" +
    "<p>These settings are missing from the build:</p><ul>" +
    missingConfig.map((name) => `<li><code>${name}</code></li>`).join("") +
    "</ul><p>Copy <code>.env.example</code> to <code>.env</code> in the repository " +
    "root, then restart <code>npm run dev</code>. Vite only reads env files at " +
    "startup, so a running dev server will not pick up a new one.</p></main>";
  throw new Error(`Missing front end configuration: ${missingConfig.join(", ")}`);
}

const msalInstance = new PublicClientApplication(msalConfig);

// MSAL must finish initializing before React renders, otherwise the first
// render races the handling of the redirect response coming back from Entra.
void msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then(() => {
    // Put back the page they were heading for before being sent to Entra.
    restoreReturnPath();

    // A signed-in account does not automatically become the active one, and
    // every token call needs an active account.
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as { account?: AccountInfo };
        if (payload.account) msalInstance.setActiveAccount(payload.account);
      }
    });

    createRoot(container).render(
      <StrictMode>
        <ErrorBoundary>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    console.error("MSAL failed to initialize", error);
    container.textContent = "Sign-in could not start. Check the console.";
  });
